export type SidebarMenuId =
  | "Input View"
  | "Output View"
  | "SSA Dashboard"
  | "Approver Maintenance"
  | "Data Error"
  | "Support Ticket Summary"
  | "Approver"
  | "SSA Control";

export type AppPermission =
  | "use_admin_categories"
  | "submit_planner"
  | "approve_workflow"
  | "manage_approvers"
  | "retry_data_error"
  | "create_support_ticket"
  | "edit_ssa_control"
  | "view_ssa_history";

export type AppRoleId =
  | "planner"
  | "approver"
  | "business_support"
  | "ssa_admin"
  | "it_support";

type RoleConfig = {
  id: AppRoleId;
  displayName: string;
  priority: number;
  defaultPath: string;
  aliases: string[];
  allowedMenus: SidebarMenuId[];
  allowedPathPrefixes: string[];
  permissions: Record<AppPermission, boolean>;
};

const normalize = (value?: string | null): string =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const ROLE_CONFIGS: RoleConfig[] = [
  {
    id: "ssa_admin",
    displayName: "SSA Admin",
    priority: 500,
    defaultPath: "/admin/ssa-dashboard",
    aliases: ["ssa_admin", "ssa admin", "ssaadmin", "appadmin", "app_admin"],
    allowedMenus: [
      "SSA Dashboard",
      "Approver Maintenance",
      "Data Error",
      "Support Ticket Summary",
      "Input View",
      "Output View",
      "Approver",
      "SSA Control",
    ],
    allowedPathPrefixes: ["/admin", "/endUserView", "/approverView", "/admin/ssa-control"],
    permissions: {
      use_admin_categories: true,
      submit_planner: true,
      approve_workflow: true,
      manage_approvers: true,
      retry_data_error: true,
      create_support_ticket: true,
      edit_ssa_control: true,
      view_ssa_history: true,
    },
  },
  {
    id: "it_support",
    displayName: "IT Support",
    priority: 400,
    defaultPath: "/admin/ssa-dashboard",
    aliases: ["it_support", "it support", "itsupport"],
    allowedMenus: [
      "SSA Dashboard",
      "SSA Control",
      "Approver Maintenance",
      "Data Error",
      "Support Ticket Summary",
      "Input View",
      "Output View",
      "Approver",
    ],
    allowedPathPrefixes: ["/admin", "/admin/ssa-control", "/endUserView", "/approverView"],
    permissions: {
      use_admin_categories: false,
      submit_planner: false,
      approve_workflow: false,
      manage_approvers: false,
      retry_data_error: false,
      create_support_ticket: true,
      edit_ssa_control: false,
      view_ssa_history: true,
    },
  },
  {
    id: "business_support",
    displayName: "Business Super User",
    priority: 300,
    defaultPath: "/admin/ssa-dashboard",
    aliases: [
      "business_support",
      "business support",
      "business super user",
      "admin",
    ],
    allowedMenus: [
      "SSA Dashboard",
      "Approver Maintenance",
      "Data Error",
      "Support Ticket Summary",
    ],
    allowedPathPrefixes: [
      "/admin/ssa-dashboard",
      "/admin/adminView",
      "/admin/data-error",
      "/admin/support-ticket-summary",
    ],
    permissions: {
      use_admin_categories: true,
      submit_planner: true,
      approve_workflow: true,
      manage_approvers: true,
      retry_data_error: true,
      create_support_ticket: true,
      edit_ssa_control: false,
      view_ssa_history: false,
    },
  },
  {
    id: "approver",
    displayName: "Approver",
    priority: 200,
    defaultPath: "/approverView",
    aliases: ["approver"],
    allowedMenus: ["Input View", "Output View", "Approver"],
    allowedPathPrefixes: ["/endUserView", "/approverView"],
    permissions: {
      use_admin_categories: false,
      submit_planner: true,
      approve_workflow: true,
      manage_approvers: false,
      retry_data_error: false,
      create_support_ticket: true,
      edit_ssa_control: false,
      view_ssa_history: false,
    },
  },
  {
    id: "planner",
    displayName: "Planner",
    priority: 100,
    defaultPath: "/endUserView/input",
    aliases: ["planner", "end_user", "end user"],
    allowedMenus: ["Input View", "Output View"],
    allowedPathPrefixes: ["/endUserView"],
    permissions: {
      use_admin_categories: false,
      submit_planner: true,
      approve_workflow: false,
      manage_approvers: false,
      retry_data_error: false,
      create_support_ticket: true,
      edit_ssa_control: false,
      view_ssa_history: false,
    },
  },
];

const findRoleConfig = (role?: string | null): RoleConfig | undefined => {
  const input = normalize(role);
  if (!input) return undefined;

  return ROLE_CONFIGS.find((cfg) =>
    cfg.aliases.some((alias) => normalize(alias) === input),
  );
};

export const resolveRoleId = (role?: string | null): AppRoleId | null =>
  findRoleConfig(role)?.id ?? null;

export const getRoleDisplayName = (role?: string | null): string =>
  findRoleConfig(role)?.displayName ?? String(role || "");

export const getDefaultPathForRole = (role?: string | null): string =>
  findRoleConfig(role)?.defaultPath ?? "/endUserView/input";

export const getAllowedMenusForRole = (role?: string | null): SidebarMenuId[] =>
  findRoleConfig(role)?.allowedMenus ?? [];

export const canAccessPathForRole = (
  role: string | null | undefined,
  pathName: string,
): boolean => {
  const cfg = findRoleConfig(role);
  if (!cfg) return true;

  return cfg.allowedPathPrefixes.some((prefix) => {
    if (pathName === prefix) return true;
    return pathName.startsWith(`${prefix}/`);
  });
};

export const hasRolePermission = (
  role: string | null | undefined,
  permission: AppPermission,
): boolean => {
  const cfg = findRoleConfig(role);
  return Boolean(cfg?.permissions?.[permission]);
};

export const resolveHighestPriorityRole = (
  roleClaims: string[],
): AppRoleId | null => {
  const resolved = roleClaims
    .map((claim) => findRoleConfig(claim))
    .filter(Boolean) as RoleConfig[];

  if (!resolved.length) return null;

  const highest = resolved.sort((a, b) => b.priority - a.priority)[0];
  return highest.id;
};
